import { Box, Button, Divider, InputAdornment, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import type { ButtonProps } from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import type { TextFieldProps } from '@mui/material/TextField';
import React, { forwardRef, useId } from 'react';

const SUMMARY_BACKGROUND = '#f3ecfb';
const SECTION_LINE = '#e4dcf2';

const START_ADORNMENT_SX = {
  height: 'auto',
  maxHeight: 'none',
  alignSelf: 'stretch',
  alignItems: 'center',
  paddingRight: '8px',
  borderRight: '1px solid',
  borderColor: 'grey.400',
  '& .MuiTypography-root': { color: 'text.secondary' },
};

export const useFormFieldSize = (): 'small' | 'medium' => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true }) ? 'medium' : 'small';
};

type FieldLabelProps = {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
};

export const FieldLabel: React.FC<FieldLabelProps> = ({ label, htmlFor, required = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '6px' }}>
    <Typography component="label" htmlFor={htmlFor} variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
      {label}
    </Typography>
    {required && (
      <Box component="span" aria-hidden sx={{ color: 'error.main' }}>
        *
      </Box>
    )}
  </Box>
);

type FieldHelperProps = {
  text?: React.ReactNode;
  counter?: string;
};

const FieldHelper: React.FC<FieldHelperProps> = ({ text, counter }) => (
  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
    <span>{text}</span>
    {counter && <span>{counter}</span>}
  </Box>
);

export type FormFieldProps = Omit<TextFieldProps, 'label' | 'error' | 'helperText' | 'required'> & {
  label: React.ReactNode;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: string | null;
  maxLength?: number;
  valueLength?: number;
  showCounter?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  fullRow?: boolean;
};

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      maxLength,
      valueLength,
      showCounter = false,
      startAdornment,
      endAdornment,
      fullRow = false,
      id,
      slotProps,
      size,
      ...textFieldProps
    },
    ref
  ) => {
    const generatedId = useId();
    const responsiveSize = useFormFieldSize();
    const inputId = id ?? `form-field-${generatedId}`;
    const counter = showCounter && maxLength ? `${valueLength ?? 0}/${maxLength}` : undefined;
    const helper = error ?? helperText;
    const input = asObject(slotProps?.input);
    const htmlInput = asObject(slotProps?.htmlInput);

    return (
      <Box sx={{ gridColumn: fullRow ? '1 / -1' : undefined, minWidth: 0 }}>
        <FieldLabel label={label} htmlFor={inputId} required={required} />
        <TextField
          ref={ref}
          id={inputId}
          variant="outlined"
          fullWidth
          size={size ?? responsiveSize}
          error={!!error}
          helperText={helper || counter ? <FieldHelper text={helper} counter={counter} /> : undefined}
          {...textFieldProps}
          slotProps={{
            ...slotProps,
            input: {
              ...input,
              ...(startAdornment && {
                startAdornment: (
                  <InputAdornment position="start" sx={START_ADORNMENT_SX}>
                    {startAdornment}
                  </InputAdornment>
                ),
              }),
              ...(endAdornment && { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> }),
            },
            htmlInput: {
              ...htmlInput,
              ...(maxLength && { maxLength }),
              ...(required && { 'aria-required': true }),
            },
          }}
        />
      </Box>
    );
  }
);

FormField.displayName = 'FormField';

export const FormGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      columnGap: '18px',
      rowGap: '12px',
      alignItems: 'start',
    }}>
    {children}
  </Box>
);

export const FormSection: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="section">
    <Typography
      variant="caption"
      component="h3"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '0 0 10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'primary.main',
        '&::after': { content: '""', flex: 1, height: '1px', backgroundColor: SECTION_LINE },
      }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export type ReadOnlySummaryItem = {
  label: string;
  value?: React.ReactNode;
  fullRow?: boolean;
};

export const ReadOnlySummary: React.FC<{ items: ReadOnlySummaryItem[]; emptyValue?: React.ReactNode }> = ({
  items,
  emptyValue = '',
}) => (
  <Box
    component="dl"
    sx={{
      margin: 0,
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: SUMMARY_BACKGROUND,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
      gap: '6px 16px',
    }}>
    {items.map(({ label, value, fullRow }) => (
      <Box key={label} sx={{ gridColumn: fullRow ? '1 / -1' : undefined, minWidth: 0 }}>
        <Typography
          component="dt"
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ margin: 0, fontWeight: 500, overflowWrap: 'anywhere' }}>
          {value || emptyValue}
        </Typography>
      </Box>
    ))}
  </Box>
);

export type FormActionsProps = {
  cancelLabel: React.ReactNode;
  submitLabel: React.ReactNode;
  onCancel: () => void;
  submitProps?: ButtonProps;
  sticky?: boolean;
};

export const FormActions: React.FC<FormActionsProps> = ({
  cancelLabel,
  submitLabel,
  onCancel,
  submitProps = {},
  sticky = false,
}) => (
  <Box
    sx={{
      marginTop: '24px',
      ...(sticky && {
        position: 'sticky',
        bottom: 0,
        marginInline: '-16px',
        padding: '0 16px 16px',
        backgroundColor: 'background.paper',
        zIndex: 2,
      }),
    }}>
    <Divider sx={{ backgroundColor: 'primary.main', marginBottom: '16px' }} />
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
      <Button variant="outlined" onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button variant="contained" type="submit" color="primary" {...submitProps}>
        {submitLabel}
      </Button>
    </Stack>
  </Box>
);

export default FormField;
